import { supabase } from '@/lib/supabase/client';

/**
 * Student-stats query layer.
 *
 * Pure async functions. React-free. Wrap in hooks (see
 * src/hooks/useStudentStats.ts) when you need TanStack-Query caching.
 *
 * Every query relies on RLS — there is no `eq('user_id', ...)` filter
 * because the policies on `ayah_reads`, `reading_progress`, and
 * `bookmarks` already restrict reads to `auth.uid() = user_id`. The
 * frontend just queries normally and the server narrows the result set.
 */

/**
 * Total ayahs the signed-in user has marked read across all surahs.
 * Uses Supabase's exact-count head request so no rows are transferred.
 */
export async function getTotalAyahReads(): Promise<number> {
  const { count, error } = await supabase
    .from('ayah_reads')
    .select('*', { count: 'exact', head: true });
  if (error) throw error;
  return count ?? 0;
}

/**
 * Latest reading position. Maintained by `useRecordSurahOpen` on every
 * SurahReader mount; one row per user (primary key on user_id), so we
 * use `maybeSingle()` and return null when the user has never opened a
 * surah yet.
 */
export type ReadingProgress = {
  user_id: string;
  last_surah_id: number;
  last_ayah_number: number;
  updated_at: string;
};

export async function getReadingProgress(): Promise<ReadingProgress | null> {
  const { data, error } = await supabase
    .from('reading_progress')
    .select('user_id, last_surah_id, last_ayah_number, updated_at')
    .maybeSingle();
  if (error) throw error;
  return data as ReadingProgress | null;
}

/**
 * Counts of marked-read ayahs grouped by surah. Returns a Map<surahId, count>.
 *
 * The supabase-js client doesn't support SQL GROUP BY directly, so we
 * fetch the (user_id, surah_id) projection and group in JS. Supabase REST
 * pages results by default, so this must explicitly page through all rows;
 * otherwise users past the first page can see completed surahs undercounted.
 */
export async function getReadsByAllSurahs(): Promise<Map<number, number>> {
  const pageSize = 1000;
  let from = 0;
  const rows: Array<{ surah_id: number }> = [];

  while (true) {
    const { data, error } = await supabase
      .from('ayah_reads')
      .select('surah_id')
      .range(from, from + pageSize - 1);
    if (error) throw error;

    const page = (data ?? []) as Array<{ surah_id: number }>;
    rows.push(...page);
    if (page.length < pageSize) break;
    from += pageSize;
  }

  const counts = new Map<number, number>();
  for (const row of rows) {
    const sid = row.surah_id;
    counts.set(sid, (counts.get(sid) ?? 0) + 1);
  }
  return counts;
}

/**
 * Compute the user's reading streak in their *local* timezone.
 *
 * Definition:
 *   - A "day" is a single calendar day in the browser's local timezone.
 *   - The current streak is the count of consecutive days ending at
 *     today (or yesterday, if today has no reads yet — so the streak
 *     doesn't break mid-day before the user has read).
 *   - The longest streak is the maximum consecutive-day run across the
 *     entire history.
 *
 * We fetch `read_at` for every read row (RLS already filters by user),
 * map to local YYYY-MM-DD keys via Intl.DateTimeFormat (handles DST
 * and timezones correctly), dedupe, sort, then walk.
 */
export type StreakInfo = {
  current: number;
  longest: number;
  lastReadDate: string | null; // YYYY-MM-DD in local TZ, or null
};

export type AyahReadDetail = {
  surah_id: number;
  ayah_number: number;
  read_at: string;
};

export type JuzIndexRow = {
  surah_id: number;
  ayah_number: number;
  juz: number | null;
};

export async function getReadingStreak(): Promise<StreakInfo> {
  const { data, error } = await supabase
    .from('ayah_reads')
    .select('read_at')
    .order('read_at', { ascending: false });
  if (error) throw error;
  const rows = (data ?? []) as { read_at: string }[];
  if (rows.length === 0) {
    return { current: 0, longest: 0, lastReadDate: null };
  }

  // Local-timezone YYYY-MM-DD formatter. Intl handles DST + tz offsets;
  // doing it by hand with Date.getDate() etc. is bug-prone around midnight.
  const fmt = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const localKey = (iso: string): string => fmt.format(new Date(iso));

  const dateSet = new Set<string>();
  for (const r of rows) dateSet.add(localKey(r.read_at));

  // Sorted ascending YYYY-MM-DD strings (lexicographic == chronological).
  const dates = Array.from(dateSet).sort();
  const lastReadDate = dates[dates.length - 1] ?? null;

  // Longest run via single pass.
  let longest = 1;
  let runLength = 1;
  for (let i = 1; i < dates.length; i++) {
    const prev = dates[i - 1];
    const cur = dates[i];
    if (isNextDay(prev, cur)) {
      runLength += 1;
      if (runLength > longest) longest = runLength;
    } else {
      runLength = 1;
    }
  }

  // Current streak: walk back from today, allowing one grace day so
  // today doesn't have to have a read yet.
  const today = localKey(new Date().toISOString());
  const yesterday = addDays(today, -1);
  let current = 0;
  if (dateSet.has(today)) {
    current = 1;
    let cursor = today;
    while (dateSet.has(addDays(cursor, -1))) {
      current += 1;
      cursor = addDays(cursor, -1);
    }
  } else if (dateSet.has(yesterday)) {
    current = 1;
    let cursor = yesterday;
    while (dateSet.has(addDays(cursor, -1))) {
      current += 1;
      cursor = addDays(cursor, -1);
    }
  }

  return { current, longest, lastReadDate };
}

/** Total bookmarks for the signed-in user. */
export async function getBookmarksCount(): Promise<number> {
  const { count, error } = await supabase
    .from('bookmarks')
    .select('*', { count: 'exact', head: true });
  if (error) throw error;
  return count ?? 0;
}

/**
 * Full marked-read detail set for dashboard rollups. Paged explicitly
 * because active students can exceed Supabase REST's default row limit.
 */
export async function getAyahReadDetails(): Promise<AyahReadDetail[]> {
  const pageSize = 1000;
  let from = 0;
  const rows: AyahReadDetail[] = [];

  while (true) {
    const { data, error } = await supabase
      .from('ayah_reads')
      .select('surah_id, ayah_number, read_at')
      .order('read_at', { ascending: false })
      .range(from, from + pageSize - 1);
    if (error) throw error;

    const page = (data ?? []) as AyahReadDetail[];
    rows.push(...page);
    if (page.length < pageSize) break;
    from += pageSize;
  }

  return rows;
}

/**
 * Compact Quran index used to map read ayahs to their juz. The Quran has
 * 6,236 ayahs, so this is still small, but we page for production safety.
 */
export async function getQuranJuzIndex(): Promise<JuzIndexRow[]> {
  const pageSize = 1000;
  let from = 0;
  const rows: JuzIndexRow[] = [];

  while (true) {
    const { data, error } = await supabase
      .schema('quran_core')
      .from('ayahs')
      .select('surah_id, ayah_number, juz')
      .order('surah_id', { ascending: true })
      .order('ayah_number', { ascending: true })
      .range(from, from + pageSize - 1);
    if (error) throw error;

    const page = (data ?? []) as JuzIndexRow[];
    rows.push(...page);
    if (page.length < pageSize) break;
    from += pageSize;
  }

  return rows;
}

// ----- helpers -----

function isNextDay(prev: string, cur: string): boolean {
  return addDays(prev, 1) === cur;
}

function addDays(yyyyMmDd: string, delta: number): string {
  // Parse YYYY-MM-DD as a UTC date to avoid DST surprises during the
  // delta arithmetic. We add 24h * delta and re-format. The local-tz
  // representation of the source key matches the local-tz formatter
  // because the difference is integer days.
  const [y, m, d] = yyyyMmDd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + delta);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return yy + '-' + mm + '-' + dd;
}
